import { useState, useMemo, useEffect } from "react";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useQueryClient } from "@tanstack/react-query";
import {
  Plus,
  Bot,
  Package,
  Search,
  SearchX,
} from "lucide-react";
import { Button } from "@ui/components/ui/button";
import { Input } from "@ui/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@tubo/ui/components/ui/select";
import { BackHeader } from "@client/components/workbench/back-header";
import { Tabs, TabsList, TabsTrigger } from "@tubo/ui/components/ui/tabs";
import {
  useListBots,
  useDeleteBot,
  getListBotsQueryKey,
} from "@tubo/api-models/hooks/chat-bot-api.hooks";
import { groupBots } from "./-bot-groups";
import { Skeleton } from "@tubo/ui/components/ui/skeleton";
import type { ChatBotVO } from "@tubo/api-models/hooks/common.schemas";
import { AiCreateBotDialog } from "./-ai-create-dialog";
import { BotCard } from "./-bot-card";
import { ToolsTab } from "@client/components/tool-workshop/tools-tab";
import { CredentialsTab } from "@client/components/tool-workshop/credentials-tab";
import { SkillsTab } from "@client/components/tool-workshop/skills-tab";
import {
  hasRightAtLeast,
  useStaticKeysState,
} from "@client/hooks/use-nav-permissions";
import { usePermissions } from "@client/hooks/use-permissions";

type WorkshopTab = "bots" | "tools" | "skills" | "credentials";

export const Route = createFileRoute("/_app/bots/")({
  component: ChatBots,
  // tab 走 search 参数：二级页返回时可回到原 tab（可深链）
  validateSearch: (search: Record<string, unknown>): { tab?: WorkshopTab } => {
    const tab = typeof search.tab === "string" ? search.tab : undefined;
    return {
      tab: tab === "bots" || tab === "tools" || tab === "skills" || tab === "credentials" ? tab : undefined,
    };
  },
});

const WORKSHOP_TABS: Array<{ value: WorkshopTab; label: string }> = [
  { value: "bots", label: "智能体" },
  { value: "tools", label: "工具" },
  { value: "skills", label: "技能" },
  { value: "credentials", label: "凭据" },
];

function ChatBots() {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { isTenantAdmin } = usePermissions();
  const { keys: permKeys, rights, isPending: permLoading } = useStaticKeysState();
  // 功能点 tool-workshop 分级：V=页面可用；E=创作与管理自己资源；A=审核/凭据等跨属主管理。
  // 管理员（tAdm/sAdm/Owner）后端恒返回全 key + Admin，无需前端特判。
  const workshopPermitted = permKeys.has("tool-workshop");
  const canEdit = hasRightAtLeast(rights["tool-workshop"], "E");
  const canAdmin = hasRightAtLeast(rights["tool-workshop"], "A");
  const { tab: tabParam } = Route.useSearch();
  const tab: WorkshopTab = tabParam ?? "bots";
  const setTab = (next: WorkshopTab) => void navigate({ to: "/bots", search: { tab: next } });
  const handleTabValue = (value: unknown) => {
    if (value === "bots" || value === "tools" || value === "skills" || value === "credentials") setTab(value);
  };
  const [search, setSearch] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("all");

  // 页面守卫：权限未加载不跳；确认无 tool-workshop 权限回工作台（入口已隐藏，直链兜底）
  useEffect(() => {
    if (!permLoading && !workshopPermitted) {
      void navigate({ to: "/workbench" });
    }
  }, [permLoading, workshopPermitted, navigate]);

  const { data: botsResp, isPending: botsLoading, isError: botsError, refetch: reloadBots } = useListBots({ query: { enabled: workshopPermitted && !permLoading } });
  const bots: ChatBotVO[] = useMemo(() => botsResp?.bots ?? [], [botsResp]);

  const searchedBots = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return bots;
    return bots.filter(
      (bot) => bot.name?.toLowerCase().includes(q) || bot.desc?.toLowerCase().includes(q),
    );
  }, [bots, search]);

  const allGroups = useMemo(() => groupBots(searchedBots), [searchedBots]);
  const groups = useMemo(
    () => (categoryFilter === "all" ? allGroups : allGroups.filter((g) => g.key === categoryFilter)),
    [allGroups, categoryFilter],
  );
  const categoryItems = useMemo(
    () => [{ value: "all", label: "全部分类" }, ...allGroups.map((g) => ({ value: g.key, label: g.title }))],
    [allGroups],
  );
  const deleteMutation = useDeleteBot({
    mutation: {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: getListBotsQueryKey() });
      },
    },
  });

  // 守卫渲染：权限加载中不渲染内容（防按钮闪烁）；无权限交由上方 useEffect 跳转
  if (permLoading || !workshopPermitted) return null;

  return (
    <div className="flex h-full min-w-0 flex-col">
      <BackHeader
        title="智能体工坊"
        backLabel="返回"
        onBack={() => navigate({ to: "/workbench" })}
        headerExtra={
          <Tabs value={tab} onValueChange={handleTabValue}>
            {/* ml-2 抵消 headerExtra 容器的 -ml-2.5（该补偿面向模型选择器触发器） */}
            <TabsList className="ml-2">
              {WORKSHOP_TABS.filter((t) => t.value !== "credentials" || isTenantAdmin || canAdmin).map((t) => (
                <TabsTrigger key={t.value} value={t.value}>{t.label}</TabsTrigger>
              ))}
            </TabsList>
          </Tabs>
        }
        actions={
          <>
            {tab === "bots" && (
              <>
                <div className="relative">
                  <Search className="pointer-events-none absolute left-2.5 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
                  <Input
                    value={search}
                    onChange={(event) => setSearch(event.target.value)}
                    placeholder="搜索 Bot..."
                    className="h-8 w-48 pl-8"
                  />
                </div>
                <Select
                  value={categoryFilter}
                  onValueChange={(value) => setCategoryFilter(value ?? "all")}
                  items={categoryItems}
                >
                  <SelectTrigger className="h-8 w-36 text-xs">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {categoryItems.map((item) => (
                      <SelectItem key={item.value} value={item.value}>{item.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {canEdit && <AiCreateBotDialog />}
                {canEdit && (
                  <Button onClick={() => void navigate({ to: "/bots/new" })}>
                    <Plus className="size-4" />
                    创建智能体
                  </Button>
                )}
              </>
            )}
            {/* 四 tab 的新建入口统一收在页头右侧 */}
            {tab === "tools" && canEdit && (
              <Button size="sm" onClick={() => void navigate({ to: "/bots/tools/new" })}>
                <Plus className="size-4" />
                注册工具
              </Button>
            )}
            {tab === "skills" && canEdit && (
              <Button size="sm" onClick={() => void navigate({ to: "/bots/skills/new" })}>
                <Plus className="size-4" />
                新建技能
              </Button>
            )}
            {tab === "credentials" && (isTenantAdmin || canAdmin) && (
              <Button size="sm" onClick={() => void navigate({ to: "/bots/credentials/new" })}>
                <Plus className="size-4" />
                新建凭据
              </Button>
            )}
          </>
        }
      />

      {tab === "tools" && <div className="min-w-0 flex-1 overflow-y-auto"><div className="p-6"><ToolsTab canAdmin={isTenantAdmin || canAdmin} /></div></div>}
      {tab === "skills" && <div className="min-w-0 flex-1 overflow-y-auto"><div className="p-6"><SkillsTab isAdmin={isTenantAdmin} /></div></div>}
      {tab === "credentials" && (isTenantAdmin || canAdmin) && <div className="min-w-0 flex-1 overflow-y-auto"><div className="p-6"><CredentialsTab /></div></div>}

      {tab === "bots" && (
        <div className="min-w-0 flex-1 overflow-y-auto">
        <div className="p-6">
          {botsLoading && <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3" aria-label="正在加载智能体">
            {[0, 1, 2].map((key) => <Skeleton key={key} className="h-40 rounded-lg" />)}
          </div>}
          {botsError && <div className="py-12 text-center" role="alert">
            <p className="text-muted-foreground">智能体加载失败</p>
            <Button variant="outline" className="mt-3" onClick={() => void reloadBots()}>重试</Button>
          </div>}

          {!botsLoading && !botsError && groups.map((group) => (
            <div key={group.key} className="mt-5">
              <h3 className="mb-3 flex items-center gap-1.5 text-sm font-medium text-muted-foreground">
                {group.isPack && <Package className="size-3.5" aria-hidden />}
                {group.title}
              </h3>
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-4">
                {group.bots.map((bot) => (
                  <BotCard
                    key={bot.id}
                    bot={bot}
                    inPack={group.isPack}
                    onOpenStudio={(b) =>
                      navigate({
                        to: "/bots/$botId/studio",
                        params: { botId: b.id },
                      })
                    }
                    onDelete={(b) => b.id && deleteMutation.mutate({ id: b.id })}
                  />
                ))}
              </div>
            </div>
          ))}

          {!botsLoading && !botsError && groups.length === 0 && bots.length > 0 && (
            <div className="text-center py-12 text-muted-foreground">
              <SearchX className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p>没有匹配的 Bot，请调整搜索或筛选条件</p>
            </div>
          )}

          {!botsLoading && !botsError && bots.length === 0 && (
            <div className="text-center py-12 text-muted-foreground">
              <Bot className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p>暂无智能体，点击上方按钮创建</p>
            </div>
          )}
        </div>
      </div>
      )}
    </div>
  );
}
